import { useQuery } from "@tanstack/react-query";
import { Stack } from "@mui/joy";
import { getInvoiceDetails, postValidationEvent } from "@/services/api/crm/financeQa";
import Loader from "@/components/Loader/Loader";
import { AxiosError } from "axios";
import ErrorFallback from "@/components/ErrorFallback/ErrorFallback";
import InvoiceHeader from "./InvoiceHeader";
import FinanceQaTabs from "./FinanceQaTabs";
import { useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { alertChecks, validateInvoice, ValidationResponse } from "../../utility/validationChecker";
import { GetInvoiceDetailsResponseObj, IFInvoiceWithDetailsObj } from "@/types/interfaces/crm";
import { toast } from "sonner";
import { getInterfaceValidatorConfig } from "@/services/api/crm/config";
import { getAgentRisingStarLevel } from "@/services/api/interface-validator/risingStar";

interface Props {
  invoices: IFInvoiceWithDetailsObj[];
  setSelectedInvoiceIndex: (index: number) => void;
  selectedInvoiceIndex: number;
}

export default function InvoiceViewer({ invoices, setSelectedInvoiceIndex, selectedInvoiceIndex }: Props) {
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoiceId");

  const [alertsCheckerExpanded, setAlertsCheckerExpanded] = useState(true);
  const [alertsCheckerError, setAlertsCheckerError] = useState(false);
  const [alertsCheckerLoading, setAlertsCheckerLoading] = useState(false);
  const [checkResults, setCheckResults] = useState<ValidationResponse[]>([]);

  const invoiceDetailsQueryClient = useQuery({
    queryKey: ["interfaceValidatorInvoiceDetails", invoiceId],
    queryFn: () => getInvoiceDetails(invoiceId!),
    enabled: !!invoiceId,
  });

  const getInterfaceValidatorConfigQuery = useQuery({
    queryKey: ["interfaceValidatorConfig"],
    queryFn: getInterfaceValidatorConfig,
  });

  const primaryAgentNumber =
    invoiceDetailsQueryClient?.data?.data?.trip.osAgentNumber2 ||
    invoiceDetailsQueryClient?.data?.data?.trip.osAgentNumber1;

  const risingStarQuery = useQuery({
    queryKey: ["risingStarLevel", primaryAgentNumber],
    enabled: !!primaryAgentNumber,
    queryFn: () => getAgentRisingStarLevel(primaryAgentNumber!),
  });

  const validate = useCallback(async () => {
    setAlertsCheckerError(false);
    const isExpanded = alertsCheckerExpanded;
    if (isExpanded) {
      setAlertsCheckerExpanded(false);
    }
    setAlertsCheckerLoading(true);
    setCheckResults([]);
    if (invoiceDetailsQueryClient?.data) {
      const invoiceDetailsQueryData = invoiceDetailsQueryClient.data as unknown as GetInvoiceDetailsResponseObj;
      const results: ValidationResponse[] = [];
      for (let check of alertChecks) {
        const result = validateInvoice(check, {
          arInvoices: invoiceDetailsQueryData?.data?.arInvoices,
          ifInvoice: invoiceDetailsQueryData?.data?.ifInvoice,
          references: invoiceDetailsQueryData?.references,
          reservationLines: invoiceDetailsQueryData?.data?.reservations,
          interfaceValidatorConfig: getInterfaceValidatorConfigQuery.data?.data,
          risingStarLevel: risingStarQuery.data?.risingStarLevel,
        });
        results.push(result);
        setCheckResults((prev) => [...prev, result]);
      }

      // Record validation results via API
      const ifInvoice = invoiceDetailsQueryData?.data?.ifInvoice;
      if (ifInvoice) {
        const totalPassed = results.filter((r) => r.isValid).length;
        const totalFailed = results.filter((r) => !r.isValid).length;
        const failedValidations = results
          .map((r, index) => ({
            isValid: r.isValid,
            validationId: String(alertChecks[index].alertId),
            message: r.message || alertChecks[index].errorMessage,
          }))
          .filter((r) => !r.isValid)
          .map(({ validationId, message }) => ({ validationId, message }));

        try {
          await postValidationEvent({
            clInvoiceId: ifInvoice.invoiceId,
            clInvoiceNumber: ifInvoice.invoiceNumber || "",
            agentNumber: ifInvoice.bookingAgentNumber || "",
            clBranchNumber: String(ifInvoice.branchNumber),
            totalPassed,
            totalFailed,
            failedValidations: failedValidations.length > 0 ? failedValidations : null,
          });
        } catch (error) {
          toast.error("Failed to record validation event");
        }
      }
    }
    setTimeout(() => {
      setAlertsCheckerLoading(false);
      if (isExpanded) {
        setAlertsCheckerExpanded(true);
      }
    }, 500);
  }, [
    alertsCheckerExpanded,
    invoiceDetailsQueryClient?.data,
    getInterfaceValidatorConfigQuery.data,
    risingStarQuery.dataUpdatedAt,
    setCheckResults,
    setAlertsCheckerLoading,
    setAlertsCheckerExpanded,
  ]);

  useEffect(() => {
    if (risingStarQuery.isError) {
      toast.error("Failed to fetch rising star level");
    }
  }, [risingStarQuery.isError]);

  useEffect(() => {
    if (invoiceDetailsQueryClient?.isRefetching) {
      setAlertsCheckerLoading(true);
      return;
    }

    const allQueriesReady =
      invoiceDetailsQueryClient?.data &&
      !invoiceDetailsQueryClient.data.meta?.isExpectedError &&
      getInterfaceValidatorConfigQuery.data &&
      (!primaryAgentNumber || risingStarQuery.isSuccess || risingStarQuery.isError);

    if (allQueriesReady) {
      try {
        validate().catch(() => {
          setAlertsCheckerError(true);
          toast.error("Something went wrong while validating the invoice.");
          setAlertsCheckerLoading(false);
        });
      } catch (error) {
        setAlertsCheckerError(true);
        toast.error("Something went wrong while validating the invoice.");
        setAlertsCheckerLoading(false);
      }
    }
  }, [
    invoiceDetailsQueryClient?.dataUpdatedAt,
    invoiceDetailsQueryClient?.data,
    invoiceDetailsQueryClient?.isRefetching,
    getInterfaceValidatorConfigQuery.data,
    primaryAgentNumber,
    risingStarQuery.isSuccess,
    risingStarQuery.isError,
  ]);

  return (
    <Stack
      sx={{
        flex: 2,
        minWidth: 0,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: 1,
        paddingTop: "100px !important",
        p: 3,
        border: "1px solid #e0e0e0",
        gap: 3,
      }}
    >
      {invoiceDetailsQueryClient.isError ||
      invoiceDetailsQueryClient.isRefetchError ||
      invoiceDetailsQueryClient.data?.meta?.isExpectedError ? (
        <ErrorFallback
          text={
            invoiceDetailsQueryClient.data?.meta?.isExpectedError
              ? invoiceDetailsQueryClient.data.meta.message
              : ((invoiceDetailsQueryClient.error as AxiosError).response?.data as any)?.error
          }
          retry={invoiceDetailsQueryClient.refetch}
        />
      ) : invoiceDetailsQueryClient.isLoading ? (
        <Loader />
      ) : (
        <>
          <InvoiceHeader
            invoices={invoices}
            selectedInvoiceIndex={selectedInvoiceIndex}
            setSelectedInvoiceIndex={setSelectedInvoiceIndex}
            checkResults={checkResults}
            invoiceDetailsQueryClient={invoiceDetailsQueryClient}
          />
          <FinanceQaTabs
            alertsCheckerError={alertsCheckerError}
            tripId={invoiceDetailsQueryClient.data?.data?.trip?.tripId}
            invoiceDetailsQueryClient={invoiceDetailsQueryClient}
            alertsCheckerExpanded={alertsCheckerExpanded}
            setAlertsCheckerExpanded={setAlertsCheckerExpanded}
            alertsCheckerLoading={alertsCheckerLoading}
            checkResults={checkResults}
          />
        </>
      )}
    </Stack>
  );
}
